import cjaAnalytics from 'yoda-core-components/lib/helpers/Analytics/CJAAnalytics';
import { buildProductData } from 'yoda-core-components/lib/helpers/Analytics/CJAProductBuilder';
import analyticsActions from 'yoda-site-components/lib/actionTypes/AnalyticsActionTypes';
import { ADD_SITE_TO_LIST_EVENT } from 'yoda-site-components/lib/actionTypes/SavedListToolTip';
import { mapProductFromState, filterToSelectedLot } from 'yoda-mini-pdp/lib/helpers/CjaHelper';
import {
    buildAddToCartPayload,
    buildProductOOSPayload,
    buildErrorPayload,
    buildLinkClickPayload,
} from 'yoda-mini-pdp/lib/helpers/CjaPayloadBuilders';
import {
    PDP_PAGE_LOAD_READY,
    PDP_CART_ADD_ANALYTICS,
    BUNDLE_PDP_CART_ADD_ANALYTICS,
    PDP_CART_UPDATE_ANALYTICS,
    PDP_PRODUCT_OOS_ANALYTICS,
    PDP_FORM_ERROR_ANALYTICS,
    PDP_TOOL_CLICK_ANALYTICS,
    PDP_SAMEDAY_PICKUP_ANALYTICS,
    PDP_CHECK_AVAILABILITY_ANALYTICS,
    PDP_OPEN_MODAL_ANALYTICS,
    PDP_CHANGE_ZIPCODE_ANALYTICS,
    PDP_SHOP_THIS_LOOK_CLICK_EVENT,
    PDP_SHOP_THE_LOOK_MINI_PDP_CLICK_EVENT,
    PDP_SHOP_THE_SET_CLICK_EVENT,
    PDP_PSC_AVAILABILITY,
    PDP_CARE_PLAN_VIEW_ANALYTICS,
    PDP_CARE_PLAN_ACCEPT_ANALYTICS,
    SEARCH_STORES_CLICK_EVENT,
    PDP_PAGE_LOAD_PRODUCT_OOS,
    PDP_VIDEO_PLAYER_ANALYTICS,
    PDP_ENABLE_VTO,
} from '../actionTypes/AnalyticsActionTypes';
import {
    buildPageViewPayload,
    buildSelectOptionsPDPPayload,
    buildSaveProductPayload,
    stripZeroQuantityPriceFields,
} from '../helpers/CjaPayloadBuilders';

let lastSelectOptionsSKU = null;

const CJAMiddleware = (store) => (next) => (action) => {
    if (typeof window === 'undefined') return next(action);
    if (!cjaAnalytics.isEnabled()) return next(action);

    const state = store.getState();
    let eventName = null;
    let eventData = null;

    switch (action.type) {
        case PDP_PAGE_LOAD_READY:
            eventName = 'pageView';
            eventData = buildPageViewPayload(state);
            break;

        case PDP_CART_ADD_ANALYTICS:
        case BUNDLE_PDP_CART_ADD_ANALYTICS:
            eventName = 'add_to_cart';
            eventData = buildAddToCartPayload(state, action.payload || {}, 'addtocart');
            break;

        case PDP_CART_UPDATE_ANALYTICS:
            eventData = buildAddToCartPayload(state, action.payload || {}, 'addtocart:update cart');
            eventName = eventData.event_name;
            break;

        case PDP_PRODUCT_OOS_ANALYTICS:
            eventName = 'linkClick';
            eventData = buildProductOOSPayload(state);
            break;

        case PDP_VIDEO_PLAYER_ANALYTICS:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'product media',
                action.payload?.videoName || action.payload?.videoId || '',
                'videoLoaded',
                { linkRegion: 'product media' }
            );
            break;

        case PDP_FORM_ERROR_ANALYTICS:
            eventName = 'error';
            eventData = buildErrorPayload(action.payload);
            break;

        case PDP_TOOL_CLICK_ANALYTICS: {
            const toolName = action.payload || '';
            const linkRegion =
                // eslint-disable-next-line no-nested-ternary
                toolName.includes('image zoom') || toolName === 'alternate images'
                    ? 'product media'
                    : toolName.includes('expand') || toolName.includes('collapse')
                    ? 'product information'
                    : 'sticky header';
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'product details',
                toolName,
                `pdpToolClick:${toolName}`,
                {
                    linkRegion,
                }
            );
            break;
        }

        case analyticsActions.PDP_KA_CLICK_ANALYTICS: {
            const kaToolName = action.payload?.page?.pageInfo?.pdpToolName || '';
            eventName = 'linkClick';
            eventData = buildLinkClickPayload('pdp', kaToolName, 'pdpKAClick', {
                linkRegion: 'pdp:knowledge assistant',
                linkId: kaToolName,
            });
            break;
        }

        case PDP_SAMEDAY_PICKUP_ANALYTICS:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'delivery options',
                'same day pickup',
                'sameDayPickup',
                { linkRegion: 'delivery options' }
            );
            break;

        case PDP_CHECK_AVAILABILITY_ANALYTICS:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'delivery options',
                'check availability',
                `checkAvailability:${(state.productDetails || {}).id || ''}`,
                { linkRegion: 'delivery options' }
            );
            break;

        case PDP_OPEN_MODAL_ANALYTICS: {
            const modalPayload = action.payload || {};
            const { modalName = '', linkId = '', linkText = '' } =
                typeof modalPayload === 'object' ? modalPayload : { modalName: modalPayload };
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'pdp',
                linkText || modalName,
                `openModal:${modalName}`,
                {
                    linkRegion: `pdp:${modalName}`,
                    linkId,
                }
            );
            break;
        }

        case PDP_CHANGE_ZIPCODE_ANALYTICS:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'delivery options',
                'change zip code',
                'changeZipCode',
                { linkRegion: 'delivery options' }
            );
            break;

        case analyticsActions.PDP_ALL_SKU_SELECTED: {
            const skuForSelect = action.payload?.productSKU || '';
            if (skuForSelect && skuForSelect === lastSelectOptionsSKU) break;
            lastSelectOptionsSKU = skuForSelect;
            eventName = 'selectoptionspdp';
            eventData = buildSelectOptionsPDPPayload(state, action.payload || {});
            break;
        }

        case PDP_CARE_PLAN_VIEW_ANALYTICS: {
            const viewMapped = mapProductFromState(state);
            eventName = 'linkClick';
            eventData = buildLinkClickPayload('care plan', 'care plan view', 'carePlanView', {
                linkRegion: 'care plan',
                products: stripZeroQuantityPriceFields(
                    buildProductData([
                        {
                            ...viewMapped,
                            ...filterToSelectedLot(viewMapped),
                        },
                    ])
                ),
            });
            break;
        }

        case PDP_CARE_PLAN_ACCEPT_ANALYTICS: {
            const acceptMapped = mapProductFromState(state);
            eventName = 'linkClick';
            eventData = buildLinkClickPayload('care plan', 'care plan accept', 'carePlanAccept', {
                linkRegion: 'care plan',
                products: stripZeroQuantityPriceFields(
                    buildProductData([
                        {
                            ...acceptMapped,
                            ...filterToSelectedLot(acceptMapped),
                        },
                    ])
                ),
            });
            break;
        }

        case SEARCH_STORES_CLICK_EVENT: {
            const searchText = action.payload || '';
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'store locator',
                searchText,
                `storesearch:${searchText}`,
                { linkRegion: 'store locator' }
            );
            break;
        }

        case PDP_SHOP_THIS_LOOK_CLICK_EVENT:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'shop the look',
                action.payload?.linkText || action.payload?.moduleName || '',
                `shopTheLook:${action.payload?.shopTheLookID || ''}`,
                {
                    linkId: action.payload?.shopTheLookID || '',
                    linkRegion: 'pdp',
                    linkPosition: action.payload?.linkPosition ?? action.payload?.position ?? 0,
                }
            );
            break;

        case PDP_SHOP_THE_LOOK_MINI_PDP_CLICK_EVENT:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'shop the look',
                action.payload?.linkText || action.payload?.moduleName || '',
                `shopTheLookDetail:${action.payload?.shopTheLookID || ''}:${
                    action.payload?.shopTheLookPPID || ''
                }`,
                {
                    linkId: action.payload?.shopTheLookID || '',
                    linkRegion: 'mini pdp',
                    linkPosition: action.payload?.linkPosition ?? action.payload?.position ?? 0,
                }
            );
            break;

        case PDP_SHOP_THE_SET_CLICK_EVENT:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'shop the set',
                action.payload?.linkText || action.payload?.moduleName || '',
                `shopTheSet:${action.payload?.shopTheSetID || ''}:${
                    action.payload?.shopTheSetPPID || ''
                }`,
                {
                    linkId: action.payload?.shopTheSetID || '',
                    linkRegion: 'shop the set',
                    linkPosition: action.payload?.linkPosition ?? 0,
                }
            );
            break;

        case PDP_ENABLE_VTO:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload('product media', 'vto', 'vto', {
                linkRegion: 'product media',
            });
            break;

        case PDP_PSC_AVAILABILITY:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload(
                'product details',
                action.payload?.linktext || '',
                action.payload?.name || '',
                { linkRegion: 'product details' }
            );
            break;

        case PDP_PAGE_LOAD_PRODUCT_OOS:
            eventName = 'linkClick';
            eventData = buildLinkClickPayload('pdp', 'out of stock', 'inventoryProductOOS', {
                linkRegion: 'product details',
                products: stripZeroQuantityPriceFields(
                    buildProductData([
                        {
                            ...mapProductFromState(state),
                            inventoryStatus: 'out_of_stock',
                        },
                    ])
                ),
            });
            break;

        case ADD_SITE_TO_LIST_EVENT:
            eventName = 'save_product';
            eventData = buildSaveProductPayload(action.payload);
            break;

        default:
            break;
    }

    if (eventName && eventData) {
        cjaAnalytics.track(eventName, eventData);
    }

    return next(action);
};

export default CJAMiddleware;
